import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import LoadingState from "../components/LoadingState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Globe, Eye, EyeOff, ExternalLink, Loader2, ShieldCheck, AlertTriangle, Info, Trash2 } from "lucide-react";
import { api } from "../lib/api";

function StatusPill({ status }) {
    if (!status) return <span className="text-xs text-[#94A3B8]">Mai testata</span>;
    const map = {
        success: ["bg-[#DCFCE7] text-[#166534]", "Chiave valida"],
        invalid_key: ["bg-[#FEE2E2] text-[#991B1B]", "Chiave non valida"],
        quota_exceeded: ["bg-[#FEF3C7] text-[#92400E]", "Quota esaurita"],
        error: ["bg-[#FEE2E2] text-[#991B1B]", "Errore"],
        unexpected: ["bg-[#FEF3C7] text-[#92400E]", "Risposta inattesa"],
    };
    const [cls, label] = map[status] || ["bg-[#F1F5F9] text-[#64748B]", status];
    return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

export default function WebRiskConfigPage() {
    const nav = useNavigate();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [apiKey, setApiKey] = useState("");
    const [showKey, setShowKey] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/integrations/webrisk");
            setState(data);
            setEnabled(!!data.is_enabled);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { is_enabled: enabled };
            if (apiKey.trim()) payload.api_key = apiKey.trim();
            const { data } = await api.put("/admin/integrations/webrisk", payload);
            setState(data);
            setApiKey("");
            toast.success("Configurazione salvata");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/admin/integrations/webrisk/test");
            setTestResult(data);
            if (data.outcome === "success") {
                toast.success("Test connessione riuscito");
            } else if (data.outcome === "invalid_key") {
                toast.error("Chiave API non valida");
            } else if (data.outcome === "quota_exceeded") {
                toast.error("Quota API esaurita");
            } else {
                toast.error(`Test fallito: ${data.outcome}`);
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore durante il test");
        } finally {
            setTesting(false);
        }
    };

    const removeKey = async () => {
        if (!window.confirm("Rimuovere la chiave API e disabilitare l'integrazione?")) return;
        try {
            const { data } = await api.delete("/admin/integrations/webrisk");
            setState(data);
            setEnabled(false);
            setApiKey("");
            toast.success("Integrazione disabilitata");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    if (loading) return <LoadingState />;

    const usage = state?.usage_this_month ?? 0;
    const usagePct = Math.min(100, (usage / 100000) * 100);

    return (
        <div className="space-y-4 max-w-2xl">
            <PageHeader
                title="Google Web Risk"
                subtitle="Verifica esterna di reputazione URL"
                breadcrumbs={[{ label: "Integrazioni", href: "/integrations" }, { label: "Google Web Risk" }]}
            />

            {/* State summary */}
            <div className="bg-white border border-[#DCE3EE] rounded-xl p-5" data-testid="webrisk-status-panel">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0B1633] text-white flex items-center justify-center">
                        <Globe className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-[#172033]">Stato integrazione</div>
                        <div className="text-xs text-[#64748B]">
                            {state?.key_configured
                                ? (enabled ? "Chiave configurata · attiva" : "Chiave configurata · disabilitata")
                                : "Nessuna chiave configurata"}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Ultimo test</div>
                        <StatusPill status={state?.last_test_status} />
                        {state?.last_tested_at && (
                            <div className="text-[10px] text-[#94A3B8] mt-1">
                                {new Date(state.last_tested_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}
                            </div>
                        )}
                        {state?.key_fingerprint && (
                            <div className="text-[10px] text-[#64748B] mt-1 font-mono" data-testid="webrisk-key-fingerprint">
                                Chiave: {state.key_fingerprint}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Uso mese corrente</div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-sm font-semibold text-[#172033]" data-testid="webrisk-usage-count">
                                {usage.toLocaleString("it-IT")}
                            </span>
                            <span className="text-[11px] text-[#64748B]">/ 100.000 (gratis)</span>
                        </div>
                        <div className="w-full bg-[#F1F5F9] rounded-full h-1 mt-1">
                            <div
                                className={`h-1 rounded-full ${usagePct > 90 ? "bg-[#DC2626]" : usagePct > 75 ? "bg-[#F59E0B]" : "bg-[#2563EB]"}`}
                                style={{ width: `${usagePct}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Local analysis (always active) */}
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-[#16A34A] mt-0.5 shrink-0" />
                <div className="flex-1">
                    <div className="text-sm font-semibold text-[#166534]">Analisi locale euristica sempre attiva</div>
                    <div className="text-xs text-[#166534]/80 mt-1">
                        Verifiche continue di Punycode, shorteners, IP literal, homoglyph e caratteri confusabili
                        indipendenti dall&apos;integrazione esterna. Nessuna dipendenza cloud.
                    </div>
                </div>
            </div>

            {/* Config form */}
            <div className="bg-white border border-[#DCE3EE] rounded-xl p-5 space-y-4">
                <div>
                    <Label htmlFor="wr-key" className="text-sm font-semibold text-[#172033]">
                        Chiave API Google Web Risk
                    </Label>
                    <div className="mt-2 relative">
                        <Input
                            id="wr-key"
                            type={showKey ? "text" : "password"}
                            data-testid="webrisk-api-key-input"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder={state?.key_configured ? "•••••••• (lascia vuoto per non modificare)" : "AIzaSy..."}
                            className="pr-10 h-10 font-mono text-sm"
                        />
                        <button
                            type="button"
                            data-testid="webrisk-toggle-visibility"
                            onClick={() => setShowKey(!showKey)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#172033]"
                        >
                            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <a
                        href="https://cloud.google.com/web-risk/docs/quickstart"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-[#2563EB] mt-1 inline-flex items-center gap-1 hover:underline"
                    >
                        Come ottenere una chiave API <ExternalLink className="w-3 h-3" />
                    </a>
                </div>

                <div className="flex items-start justify-between gap-3 pt-2 border-t border-[#DCE3EE]">
                    <div className="flex-1">
                        <Label htmlFor="wr-enabled" className="text-sm font-medium text-[#172033] cursor-pointer">
                            Abilita integrazione
                        </Label>
                        <p className="text-[11px] text-[#64748B] mt-1">
                            Quando attiva, ogni URL estratto dalle PEC viene verificato anche dal servizio esterno.
                        </p>
                    </div>
                    <Switch
                        id="wr-enabled"
                        data-testid="webrisk-enabled-toggle"
                        checked={enabled}
                        onCheckedChange={setEnabled}
                    />
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-[#DCE3EE]">
                    <Button
                        data-testid="webrisk-save-btn"
                        onClick={save}
                        disabled={saving}
                        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
                    </Button>
                    <Button
                        variant="outline"
                        data-testid="webrisk-test-btn"
                        onClick={runTest}
                        disabled={testing || !state?.key_configured}
                    >
                        {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        Test connessione
                    </Button>
                    {state?.key_configured && (
                        <Button
                            variant="outline"
                            onClick={removeKey}
                            data-testid="webrisk-remove-btn"
                            className="text-[#DC2626] border-[#FCA5A5]"
                        >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Rimuovi chiave
                        </Button>
                    )}
                    <Button variant="ghost" onClick={() => nav("/integrations")}>Torna alle integrazioni</Button>
                </div>

                {testResult && (
                    <div
                        className={`rounded-lg p-3 border ${testResult.outcome === "success" ? "bg-[#F0FDF4] border-[#BBF7D0]" : "bg-[#FEF2F2] border-[#FCA5A5]"}`}
                        data-testid="webrisk-test-result"
                    >
                        <div className="flex items-start gap-2">
                            {testResult.outcome === "success" ? (
                                <ShieldCheck className="w-4 h-4 text-[#16A34A] mt-0.5" />
                            ) : (
                                <AlertTriangle className="w-4 h-4 text-[#DC2626] mt-0.5" />
                            )}
                            <div className="text-xs">
                                <div className="font-semibold text-[#172033]">Risultato: {testResult.outcome}</div>
                                <div className="mt-1 text-[#64748B] font-mono text-[10px]">
                                    URL sicuro → {testResult.safe_url_result?.status} ({testResult.safe_url_result?.duration_ms}ms){"  "}
                                    | URL test minaccia → {testResult.threat_url_result?.status}
                                    {testResult.threat_url_result?.threat_types?.length > 0 &&
                                        ` (${testResult.threat_url_result.threat_types.join(", ")})`}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-white border border-[#DCE3EE] rounded-xl p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                <div className="text-[11px] text-[#64748B] leading-relaxed">
                    Fino a <span className="font-semibold text-[#172033]">100.000 verifiche/mese</span> sono gratuite.
                    Oltre: $0.50 ogni 1.000. Il monitoraggio dell&apos;uso locale ti aiuta a rispettare il budget.
                </div>
            </div>
        </div>
    );
}
